import { useQueryClient } from '@tanstack/react-query';
import Router, { useRouter } from 'next/router';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import {
  useTrackAgentPanelOpened,
  useTrackAgentThreadOpened,
  type WorkspaceAgentThreadReopenedStatus,
} from '@air/analytics';
import { reportErrorToBugsnag } from '@air/utils-error';

import { PrivateWorkspaceAgentActionsMenuOpenProvider } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/PrivateWorkspaceAgentActionsMenuOpenProvider';
import { PrivateWorkspaceAgentGuidedVariantProvider } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { WorkspaceAgentComposerWorkspaceProvider } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/WorkspaceAgentComposerWorkspaceProvider';
import { getWorkspaceAgentPanelZeroStateSuggestionById } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { usePrivateWorkspaceAgentAssetViewSelectionSync } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentAssetViewSelectionSync';
import { usePrivateWorkspaceAgentChatIdUrlSync } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentChatIdUrlSync';
import {
  type PrivateWorkspaceAgentComposerPillsApi,
  usePrivateWorkspaceAgentComposerPills,
} from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentComposerPills';
import { usePrivateWorkspaceAgentModifiedAssetCacheInvalidation } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentModifiedAssetCacheInvalidation';
import {
  type PrivateWorkspaceAgentSessionApi,
  usePrivateWorkspaceAgentSession,
} from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentSession';
import { usePrivateWorkspaceAgentWssSubscriptions } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentWssSubscriptions';
import { useWorkspaceAgentPanelMinimizedAnalytics } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelMinimizedAnalytics';
import { PrivateWorkspaceAgentPillClipsProvider } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPillClipsProvider';
import { PrivateWorkspaceAgentSkillsCatalogProvider } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSkillsCatalogProvider';
import { parseElicitation } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunStartedEvent,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';
import { type WorkspaceAgentSessionArchivedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionArchived';
import { type WorkspaceAgentSessionUnarchivedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUnarchived';
import { type WorkspaceAgentSessionUpdatedEvent } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentSessionUpdate';
import { usePrivateClearUploadedBatchOnScopeChange } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/usePrivateClearUploadedBatchOnScopeChange';
import {
  buildWorkspaceAgentMessagePillsById,
  createWorkspaceAgentMessagePillsCache,
  type WorkspaceAgentMessagePillsById,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentMessagePillsById';
import { flattenWorkspaceAgentHistoryPills } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentHistoryPills';
import { getLatestConfirmedUserMessageId } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getLatestConfirmedUserMessageId';
import { getWorkspaceAgentChatIdFromLocation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentChatIdFromLocation';
import {
  parseWorkspaceAgentMarketingUrlParams,
  stripWorkspaceAgentMarketingQueryParams,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/parseWorkspaceAgentMarketingUrlParams';
import {
  resolveWorkspaceAgentThreadReopenedStatus,
  toWorkspaceAgentThreadReopenedStatus,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentThreadReopenedStatus';
import {
  useWorkspaceAgentComposerSeed,
  WorkspaceAgentComposerSeedProvider,
} from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { WorkspaceAgentOnboardingRoleUrlSync } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentOnboardingRoleUrlSync';
import {
  isWorkspaceAgentPanelExpanded,
  useWorkspaceAgentPanelStore,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useAccountContext } from '~/providers/AccountProvider';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { subscribePushedRouterHref } from '~/utils/subscribePushedRouterHref';

const noop = () => undefined;

const defaultSessionValue: PrivateWorkspaceAgentSessionApi = {
  getActiveSessionId: () => null,
  getMessagesSnapshot: () => [],
  getRunsById: () => ({}),
  sessionId: null,
  sessions: [],
  pendingElicitations: [],
  getConsentRememberForSession: () => false,
  setConsentRememberForSession: noop,
  error: null,
  isSending: false,
  isCancelling: false,
  isComposerRunLocked: false,
  isLoadingSessions: false,
  isLoadingMessages: false,
  isSessionProcessing: false,
  isSessionAwaitingInput: false,
  elicitationFollowUpAssistantMessageIds: null,
  elicitationFollowUpSawSessionActivity: false,
  isAwaitingElicitationFollowUp: false,
  isAwaitingRunStart: false,
  activeRunId: null,
  runsById: {},
  handleRunFinished: noop,
  handleRunStarted: noop,
  linkActiveRunToUserMessage: noop,
  resetRunState: noop,
  cancelRun: () => Promise.resolve(),
  sendMessage: () => Promise.resolve({ didSend: false, sessionId: null }),
  selectSession: noop,
  resetSession: noop,
  adoptSessionIfIdle: noop,
  applySessionArchivedFromWss: noop,
  applySessionUnarchivedFromWss: noop,
  applySessionUpdateFromWss: noop,
  respondToElicitation: () => Promise.resolve(),
  clearElicitationFollowUp: noop,
  noteElicitationFollowUpSawSessionActivity: noop,
  refreshSessions: () => Promise.resolve(),
  fetchNextLiveSessionsPage: async () => {
    throw new Error('fetchNextLiveSessionsPage is unavailable outside PrivateWorkspaceAgentPanelProvider');
  },
  fetchNextArchivedSessionsPage: async () => {
    throw new Error('fetchNextArchivedSessionsPage is unavailable outside PrivateWorkspaceAgentPanelProvider');
  },
  hasNextLiveSessionsPage: false,
  hasNextArchivedSessionsPage: false,
  hasNextSessionsPage: false,
  isFetchingNextLiveSessionsPage: false,
  isFetchingNextArchivedSessionsPage: false,
  isFetchingNextSessionsPage: false,
  fetchNextMessagesPage: async () => {
    throw new Error('fetchNextMessagesPage is unavailable outside PrivateWorkspaceAgentPanelProvider');
  },
  hasNextMessagesPage: false,
  isFetchingNextMessagesPage: false,
  upsertMessage: noop,
  appendParsedElicitation: noop,
  removeElicitation: noop,
  workspaceId: '',
};

const defaultComposerPillsValue: PrivateWorkspaceAgentComposerPillsApi = {
  boardContextPill: null,
  clearScope: noop,
  hasExplicitSelection: false,
  scopePills: [],
};

const defaultMessagePillsValue: WorkspaceAgentMessagePillsById = new Map();

/** Its own context because it is replaced on every streamed token. See `usePrivateWorkspaceAgentSession`. */
const defaultMessagesValue: readonly WorkspaceAgentMessage[] = [];

const PrivateWorkspaceAgentSessionContext = createContext<PrivateWorkspaceAgentSessionApi>(defaultSessionValue);
const PrivateWorkspaceAgentMessagesContext = createContext<readonly WorkspaceAgentMessage[]>(defaultMessagesValue);
const PrivateWorkspaceAgentMessagePillsContext =
  createContext<WorkspaceAgentMessagePillsById>(defaultMessagePillsValue);
const PrivateWorkspaceAgentComposerPillsContext =
  createContext<PrivateWorkspaceAgentComposerPillsApi>(defaultComposerPillsValue);

export type PrivateWorkspaceAgentPanelProviderProps = {
  children: ReactNode;
  workspaceId: string;
};

export type WorkspaceAgentMarketingUrlSyncProps = {
  workspaceId: string;
};

/**
 * Consumes `agentUseCase` marketing deep links (WORK-664). Mount inside
 * `WorkspaceAgentComposerSeedProvider`. Skipped when `chatId` is present — session restore wins.
 */
const WorkspaceAgentMarketingUrlSync = memo(({ workspaceId }: WorkspaceAgentMarketingUrlSyncProps) => {
  const router = useRouter();
  const isRouterReady = router.isReady;
  const { resetSession } = usePrivateWorkspaceAgentPanelSession();
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
  const setMarketingComposerDraft = useWorkspaceAgentPanelStore((state) => state.setMarketingComposerDraft);
  const { trackAgentPanelOpened } = useTrackAgentPanelOpened();

  const [hasPanelStoreHydrated, setHasPanelStoreHydrated] = useState(() =>
    useWorkspaceAgentPanelStore.persist.hasHydrated(),
  );
  const [locationSearch, setLocationSearch] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.search,
  );

  const hasAppliedMarketingUrlRef = useRef<boolean>(false);

  useEffect(() => {
    if (useWorkspaceAgentPanelStore.persist.hasHydrated()) {
      setHasPanelStoreHydrated(true);
      return;
    }

    return useWorkspaceAgentPanelStore.persist.onFinishHydration(() => {
      setHasPanelStoreHydrated(true);
    });
  }, []);

  useEffect(() => {
    const syncSearchFromHref = (href: string) => {
      setLocationSearch(new URL(href, window.location.origin).search);
    };

    const syncSearchFromWindow = () => {
      syncSearchFromHref(window.location.href);
    };

    syncSearchFromWindow();

    const unsubscribePushedHref = subscribePushedRouterHref(syncSearchFromHref);
    Router.events.on('routeChangeComplete', syncSearchFromHref);
    window.addEventListener('popstate', syncSearchFromWindow);

    return () => {
      unsubscribePushedHref();
      Router.events.off('routeChangeComplete', syncSearchFromHref);
      window.removeEventListener('popstate', syncSearchFromWindow);
    };
  }, []);

  useEffect(() => {
    if (!isRouterReady || !hasPanelStoreHydrated || hasAppliedMarketingUrlRef.current) {
      return;
    }

    const marketingSearch = typeof window === 'undefined' ? locationSearch : window.location.search || locationSearch;
    const parsed = parseWorkspaceAgentMarketingUrlParams(marketingSearch);

    if (!parsed.hasAgentUseCaseParam) {
      return;
    }

    hasAppliedMarketingUrlRef.current = true;

    const urlChatId = getWorkspaceAgentChatIdFromLocation({ search: marketingSearch });

    stripWorkspaceAgentMarketingQueryParams('replace');

    if (urlChatId) {
      return;
    }

    if (!parsed.shouldOpenPanel) {
      return;
    }

    const wasMinimized = dock === 'minimized';

    resetSession();
    showChat();
    // The link opens a blank thread. A location left by an earlier entry would be stamped on the first send.
    useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();

    if (wasMinimized) {
      trackAgentPanelOpened({ location: 'url' });
    }

    const suggestionId = parsed.suggestionId;

    if (!suggestionId) {
      return;
    }

    const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(suggestionId);

    if (!suggestion) {
      return;
    }

    // Store the draft on the panel store so `usePrivateWorkspaceAgentMarketingComposerDraftSync`
    // can apply it after the portalled panel (and TipTap) finish mounting — a one-shot seed can be
    // lost when ui2026 width-in or provider remount replaces the editor instance.
    setMarketingComposerDraft({
      prompt: suggestion.prompt,
      workspaceId,
    });
  }, [
    dock,
    hasPanelStoreHydrated,
    isRouterReady,
    locationSearch,
    resetSession,
    setMarketingComposerDraft,
    showChat,
    trackAgentPanelOpened,
    workspaceId,
  ]);

  return null;
});

WorkspaceAgentMarketingUrlSync.displayName = 'WorkspaceAgentMarketingUrlSync';

/**
 * Selection menus live outside `WorkspaceAgentComposerSeedProvider`; hero seeds are queued on the
 * panel store and applied once the portalled panel (and composer) are expanded.
 */
type WorkspaceAgentPendingHeroSeedSyncProps = {
  workspaceId: string;
};

const WorkspaceAgentPendingHeroSeedSync = memo(({ workspaceId }: WorkspaceAgentPendingHeroSeedSyncProps) => {
  const pendingHeroComposerSeed = useWorkspaceAgentPanelStore((state) => state.pendingHeroComposerSeed);
  const clearPendingHeroComposerSeed = useWorkspaceAgentPanelStore((state) => state.clearPendingHeroComposerSeed);
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const { seedComposer } = useWorkspaceAgentComposerSeed();
  const isPanelExpanded = isWorkspaceAgentPanelExpanded({ dock });

  useEffect(() => {
    if (!pendingHeroComposerSeed || !isPanelExpanded) {
      return;
    }

    if (pendingHeroComposerSeed.workspaceId && pendingHeroComposerSeed.workspaceId !== workspaceId) {
      clearPendingHeroComposerSeed();
      useWorkspaceAgentPanelStore.getState().clearOnboardingThreadCreatedLocation();

      return;
    }

    if (pendingHeroComposerSeed.source === 'guided_flow') {
      seedComposer(pendingHeroComposerSeed.prompt, {
        boardPill: pendingHeroComposerSeed.boardPill,
        guidedFlowSubmission: pendingHeroComposerSeed.guidedFlowSubmission,
        jsonContent: pendingHeroComposerSeed.jsonContent,
        sendImmediately: pendingHeroComposerSeed.sendImmediately,
        source: 'guided_flow',
        structuredDisplay: pendingHeroComposerSeed.structuredDisplay,
        workspaceId: pendingHeroComposerSeed.workspaceId ?? workspaceId,
      });
    } else {
      seedComposer(pendingHeroComposerSeed.prompt, {
        actionsMenuTaskId: pendingHeroComposerSeed.actionsMenuTaskId,
        boardPill: pendingHeroComposerSeed.boardPill,
        jsonContent: pendingHeroComposerSeed.jsonContent,
        sendImmediately: pendingHeroComposerSeed.sendImmediately,
        source: pendingHeroComposerSeed.source,
        structuredDisplay: pendingHeroComposerSeed.structuredDisplay,
        workspaceId: pendingHeroComposerSeed.workspaceId ?? workspaceId,
      });
    }
    clearPendingHeroComposerSeed();
  }, [clearPendingHeroComposerSeed, isPanelExpanded, pendingHeroComposerSeed, seedComposer, workspaceId]);

  return null;
});

WorkspaceAgentPendingHeroSeedSync.displayName = 'WorkspaceAgentPendingHeroSeedSync';

/** Session + WSS subscriptions survive the panel being minimized. */
export const PrivateWorkspaceAgentPanelProvider = memo(
  ({ children, workspaceId }: PrivateWorkspaceAgentPanelProviderProps) => {
    const { data: account } = useAccountContext();
    const queryClient = useQueryClient();
    const { api: sessionApi, messages, scheduleLiveSessionCatchUp } = usePrivateWorkspaceAgentSession({ workspaceId });
    const composerPills = usePrivateWorkspaceAgentComposerPills();
    usePrivateWorkspaceAgentAssetViewSelectionSync();
    usePrivateClearUploadedBatchOnScopeChange(sessionApi.sessionId);
    const setPanelView = useWorkspaceAgentPanelStore((state) => state.setPanelView);
    const pendingSessionReset = useWorkspaceAgentPanelStore((state) => state.pendingSessionReset);
    const clearPendingSessionReset = useWorkspaceAgentPanelStore((state) => state.clearPendingSessionReset);
    const pendingAgentSessionId = useWorkspaceAgentPanelStore((state) => state.pendingAgentSessionId);
    const pendingAgentSessionReopenedFrom = useWorkspaceAgentPanelStore(
      (state) => state.pendingAgentSessionReopenedFrom,
    );
    const clearPendingAgentSessionId = useWorkspaceAgentPanelStore((state) => state.clearPendingAgentSessionId);
    const showChat = useWorkspaceAgentPanelStore((state) => state.showChat);
    const { trackAgentThreadOpened } = useTrackAgentThreadOpened();

    useWorkspaceAgentPanelMinimizedAnalytics();

    const getThreadReopenedStatus = useCallback(
      (targetSessionId: string): WorkspaceAgentThreadReopenedStatus | null =>
        toWorkspaceAgentThreadReopenedStatus(sessionApi.sessions.find((entry) => entry.id === targetSessionId)?.status),
      [sessionApi.sessions],
    );

    const resolveThreadReopenedStatus = useCallback(
      (targetSessionId: string) =>
        resolveWorkspaceAgentThreadReopenedStatus({
          queryClient,
          sessions: sessionApi.sessions,
          sessionId: targetSessionId,
          workspaceId,
        }),
      [queryClient, sessionApi.sessions, workspaceId],
    );

    usePrivateWorkspaceAgentChatIdUrlSync({
      getActiveSessionId: sessionApi.getActiveSessionId,
      getThreadReopenedStatus,
      isLoadingSessions: sessionApi.isLoadingSessions,
      resolveThreadReopenedStatus,
      resetSession: sessionApi.resetSession,
      selectSession: sessionApi.selectSession,
      sessionError: sessionApi.error,
      sessionId: sessionApi.sessionId,
    });

    // FAB / Start Task sit outside this provider, so they flag the store and we reset here. Session
    // → URL then drops `chatId` the same way the header's New thread button does. Layout so the
    // previous thread is not painted for a frame.
    useIsomorphicLayoutEffect(() => {
      if (!pendingSessionReset) {
        return;
      }

      sessionApi.resetSession();
      clearPendingSessionReset();
    }, [clearPendingSessionReset, pendingSessionReset, sessionApi.resetSession]);

    // The choices only belong to the blank thread that opened them; any real session ends them.
    useEffect(() => {
      if (sessionApi.sessionId) {
        useWorkspaceAgentPanelStore.getState().clearBoardTemplatesFlowBoard();
      }
    }, [sessionApi.sessionId]);

    useIsomorphicLayoutEffect(() => {
      if (!pendingAgentSessionId) {
        return;
      }

      const sessionId = pendingAgentSessionId;
      const reopenedFrom = pendingAgentSessionReopenedFrom;

      sessionApi.selectSession(sessionId);
      showChat({ focusComposer: false });
      clearPendingAgentSessionId();

      void resolveThreadReopenedStatus(sessionId).then((threadStatus) => {
        if (reopenedFrom) {
          trackAgentThreadOpened({
            is_first_run_walkthrough: false,
            reopened_from: reopenedFrom,
            session_id: sessionId,
            status: threadStatus,
            type: 'existing',
          });
          return;
        }

        trackAgentThreadOpened({
          is_first_run_walkthrough: false,
          session_id: sessionId,
          status: threadStatus,
          type: 'existing',
        });
      });
    }, [
      clearPendingAgentSessionId,
      pendingAgentSessionId,
      pendingAgentSessionReopenedFrom,
      resolveThreadReopenedStatus,
      sessionApi.selectSession,
      showChat,
      trackAgentThreadOpened,
    ]);

    // `panelView` lives in an app-lifetime store but is per-workspace; this provider is keyed by
    // workspace id, so its unmount is the workspace switch.
    useEffect(
      () => () => {
        setPanelView('chat');
        clearPendingSessionReset();
        useWorkspaceAgentPanelStore.getState().clearPendingAutomationTasksFocusId();
        useWorkspaceAgentPanelStore.getState().clearPendingAgentSessionId();
        useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();
        useWorkspaceAgentPanelStore.getState().clearBoardTemplatesFlowBoard();
      },
      [clearPendingSessionReset, setPanelView],
    );

    // The single zod pass over the thread; the cache keeps untouched rows' pill identity across
    // per-token rebuilds.
    const [pillsCache] = useState(createWorkspaceAgentMessagePillsCache);
    const historyPillsRef = useRef<readonly WorkspaceAgentMessagePill[]>();

    const pillsByMessageId = useMemo(
      () => buildWorkspaceAgentMessagePillsById({ cache: pillsCache, messages }),
      [messages, pillsCache],
    );

    const historyPills = useMemo(() => {
      const next = flattenWorkspaceAgentHistoryPills({ pillsByMessageId, previous: historyPillsRef.current });

      historyPillsRef.current = next;

      return next;
    }, [pillsByMessageId]);

    const { appendParsedElicitation } = sessionApi;

    const handleElicitationRequested = useCallback(
      (eventSessionId: string, elicitation: unknown) => {
        // Only the open thread owns a pending-elicitation list; other sessions just refresh status.
        if (eventSessionId === sessionApi.sessionId) {
          appendParsedElicitation(parseElicitation(elicitation));
        }

        void sessionApi.refreshSessions();
      },
      [appendParsedElicitation, sessionApi.refreshSessions, sessionApi.sessionId],
    );

    const handleElicitationResponded = useCallback(
      (eventSessionId: string, elicitationId: string) => {
        if (eventSessionId === sessionApi.sessionId) {
          sessionApi.removeElicitation(elicitationId);
        }

        void sessionApi.refreshSessions();
      },
      [sessionApi.removeElicitation, sessionApi.refreshSessions, sessionApi.sessionId],
    );

    const handleSessionCreated = useCallback(
      (sessionId: string) => {
        sessionApi.adoptSessionIfIdle(sessionId);
        void sessionApi.refreshSessions();
      },
      [sessionApi.adoptSessionIfIdle, sessionApi.refreshSessions],
    );

    const handleRunStarted = useCallback(
      (event: WorkspaceAgentRunStartedEvent) => {
        sessionApi.handleRunStarted(event);

        if (event.sessionId === sessionApi.getActiveSessionId()) {
          // Ref snapshot, not `messages`: closing over the per-token array would rebuild the WSS
          // subscription on every token.
          const currentMessages = sessionApi.getMessagesSnapshot();
          const hasUnconfirmedUserMessage = currentMessages.some(
            (message) => message.role === 'user' && (message.isPending || message.id.startsWith('temp:')),
          );

          if (!hasUnconfirmedUserMessage) {
            const latestConfirmedUserMessageId = getLatestConfirmedUserMessageId(currentMessages);

            if (latestConfirmedUserMessageId) {
              sessionApi.linkActiveRunToUserMessage(latestConfirmedUserMessageId);
            }
          }
        }

        void sessionApi.refreshSessions();
      },
      [sessionApi],
    );

    const handleRunFinishedBase = useCallback(
      (event: WorkspaceAgentRunFinishedEvent) => {
        sessionApi.handleRunFinished(event);
        void sessionApi.refreshSessions();
      },
      [sessionApi.handleRunFinished, sessionApi.refreshSessions],
    );

    const handleSessionArchived = useCallback(
      (event: WorkspaceAgentSessionArchivedEvent) => {
        sessionApi.applySessionArchivedFromWss(event);
      },
      [sessionApi.applySessionArchivedFromWss],
    );

    const handleSessionUnarchived = useCallback(
      (event: WorkspaceAgentSessionUnarchivedEvent) => {
        sessionApi.applySessionUnarchivedFromWss(event);
      },
      [sessionApi.applySessionUnarchivedFromWss],
    );

    const handleSessionUpdated = useCallback(
      (event: WorkspaceAgentSessionUpdatedEvent) => {
        sessionApi.applySessionUpdateFromWss(event);
      },
      [sessionApi.applySessionUpdateFromWss],
    );

    const handleMessageAddedBase = useCallback(
      (message: WorkspaceAgentMessage) => {
        sessionApi.upsertMessage(message);

        if (message.role === 'user' && !message.isPending && message.sessionId === sessionApi.getActiveSessionId()) {
          sessionApi.linkActiveRunToUserMessage(message.id, { allowRelink: true });
        }
      },
      [sessionApi],
    );

    const { handleMessageAdded, handleRunFinished } = usePrivateWorkspaceAgentModifiedAssetCacheInvalidation({
      getActiveSessionId: sessionApi.getActiveSessionId,
      getMessagesSnapshot: sessionApi.getMessagesSnapshot,
      getRunsById: sessionApi.getRunsById,
      onMessageAdded: handleMessageAddedBase,
      onRunFinished: handleRunFinishedBase,
    });

    usePrivateWorkspaceAgentWssSubscriptions({
      accountId: account?.id,
      workspaceId,
      onMessageAdded: handleMessageAdded,
      onSessionCreated: handleSessionCreated,
      onElicitationRequested: handleElicitationRequested,
      onElicitationResponded: handleElicitationResponded,
      onRunStarted: handleRunStarted,
      onRunFinished: handleRunFinished,
      onSessionArchived: handleSessionArchived,
      onSessionUnarchived: handleSessionUnarchived,
      onSessionUpdated: handleSessionUpdated,
      scheduleLiveSessionCatchUp,
    });

    return (
      <PrivateWorkspaceAgentSessionContext.Provider value={sessionApi}>
        <PrivateWorkspaceAgentMessagesContext.Provider value={messages}>
          <PrivateWorkspaceAgentComposerPillsContext.Provider value={composerPills}>
            <PrivateWorkspaceAgentMessagePillsContext.Provider value={pillsByMessageId}>
              <WorkspaceAgentComposerWorkspaceProvider workspaceId={workspaceId}>
                <PrivateWorkspaceAgentPillClipsProvider pills={historyPills} workspaceId={workspaceId}>
                  <PrivateWorkspaceAgentSkillsCatalogProvider workspaceId={workspaceId}>
                    <PrivateWorkspaceAgentActionsMenuOpenProvider>
                      <PrivateWorkspaceAgentGuidedVariantProvider sessionId={sessionApi.sessionId}>
                        <WorkspaceAgentComposerSeedProvider>
                          <WorkspaceAgentMarketingUrlSync workspaceId={workspaceId} />
                          <WorkspaceAgentOnboardingRoleUrlSync />
                          <WorkspaceAgentPendingHeroSeedSync workspaceId={workspaceId} />
                          {children}
                        </WorkspaceAgentComposerSeedProvider>
                      </PrivateWorkspaceAgentGuidedVariantProvider>
                    </PrivateWorkspaceAgentActionsMenuOpenProvider>
                  </PrivateWorkspaceAgentSkillsCatalogProvider>
                </PrivateWorkspaceAgentPillClipsProvider>
              </WorkspaceAgentComposerWorkspaceProvider>
            </PrivateWorkspaceAgentMessagePillsContext.Provider>
          </PrivateWorkspaceAgentComposerPillsContext.Provider>
        </PrivateWorkspaceAgentMessagesContext.Provider>
      </PrivateWorkspaceAgentSessionContext.Provider>
    );
  },
);

PrivateWorkspaceAgentPanelProvider.displayName = 'PrivateWorkspaceAgentPanelProvider';

/** Test harness exports — mount panel leaves with stubbed session / pills APIs in component tests. */
export const WorkspaceAgentPanelSessionContext = PrivateWorkspaceAgentSessionContext;
export const workspaceAgentPanelDefaultSessionValue = defaultSessionValue;
export const WorkspaceAgentPanelComposerPillsContext = PrivateWorkspaceAgentComposerPillsContext;
export const workspaceAgentPanelDefaultComposerPillsValue = defaultComposerPillsValue;
export const WorkspaceAgentPanelMessagePillsContext = PrivateWorkspaceAgentMessagePillsContext;
export const WorkspaceAgentPanelMessagesContext = PrivateWorkspaceAgentMessagesContext;

export const usePrivateWorkspaceAgentPanelSession = () => {
  const context = useContext(PrivateWorkspaceAgentSessionContext);

  if (context === defaultSessionValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentPanelSession must be used within PrivateWorkspaceAgentPanelProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent session context not found' });
  }

  return context;
};

/** Separate from the session API: replaced on every streamed token. Guarded like the pills map — an
 * unprovided thread would render as an empty history rather than an error. */
export const usePrivateWorkspaceAgentPanelMessages = () => {
  const context = useContext(PrivateWorkspaceAgentMessagesContext);

  if (context === defaultMessagesValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentPanelMessages must be used within PrivateWorkspaceAgentPanelProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent messages context not found' });
  }

  return context;
};

/** Guarded: an unprovided map would silently render every moniker as a truncated id. */
export const usePrivateWorkspaceAgentPanelMessagePills = () => {
  const context = useContext(PrivateWorkspaceAgentMessagePillsContext);

  if (context === defaultMessagePillsValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentPanelMessagePills must be used within PrivateWorkspaceAgentPanelProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent message pills context not found' });
  }

  return context;
};

export const usePrivateWorkspaceAgentPanelComposerPills = () => {
  const context = useContext(PrivateWorkspaceAgentComposerPillsContext);

  if (context === defaultComposerPillsValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentPanelComposerPills must be used within PrivateWorkspaceAgentPanelProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent composer pills context not found' });
  }

  return context;
};
